import React, { useMemo } from "react";
import { useSelector } from 'react-redux';
import { useTheme, Box, Typography, Chip, Card, CardContent } from "@mui/material";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { hasPermission } from 'state/checkPermission';
import { formatTZDate } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';


const FlightCard = React.memo(({ timeMode, theme, idx, f, canView, onClick }) => { 
    const flightDate = timeMode === "UTC"
        ? formatTZDate(f?.flightActualDayDep || f?.flightDate)
        : formatTZDate(f?.flightActualDayDepLocalBase || f?.flightDateLocalBase);
    
    const etd = timeMode === "UTC"
        ? f?.atd || f?.etd
        : f?.atdLocalBase || f?.etdLocalBase;

    const eta = timeMode === "UTC"
        ? f?.ata || f?.eta
        : f?.ataLocalBase || f?.etaLocalBase;

    return (
        <Card
            key={f._id}
            variant="outlined"
            sx={{
                cursor: "pointer",
                borderRadius: 2,
                border: `1px solid ${theme.palette.neutral.deep}`,
                bgcolor: theme.palette.background.default,
            }}
            onClick={() => {
                if (!canView) return;                            
                onClick(f)}
            }
        >
            <CardContent sx={{ pt: 1.5, '&:last-child': { pb: 1.5 } }}>
                <FlexBetween>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                            label={idx + 1}
                            variant="outlined"
                            color={"primary"}
                            size="small"
                            sx={{
                                fontSize: "0.75rem",
                                borderWidth: 1.5,
                                height: 20,
                                "& .MuiChip-label": {
                                    px: 1,
                                },
                            }}
                        />

                        <Typography
                            variant="body1"
                            color={theme.palette.primary.dark}
                        >
                            {f.flightDesc}
                        </Typography>
                    </Box>
                    
                    <Typography variant="body1" color="text.secondary">
                        {flightDate}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {f.flightReg} | {f.acType}
                    </Typography>
                </FlexBetween>
                
                <FlexBetween>                                
                    <Typography variant="body1" color="text.primary">
                        {etd}
                    </Typography>
                    <Typography display="flex" alignItems="center" variant="body1" color="text.primary">
                        {f.dep} <ArrowRightAltIcon/> {f.arr}
                    </Typography>
                    <Typography variant="body1" color="text.primary">
                        {eta}
                    </Typography>
                </FlexBetween> 

                <FlexBetween>                                
                    <Typography
                        variant="body1"
                        sx={{
                            color:
                            f.tdDiff < 0
                                ? theme.palette.success.main   
                                : f.tdDiff > 0
                                    ? theme.palette.warning.main   
                                    : theme.palette.text.primary,  
                        }}
                    >
                        Departure Diff: {f.tdDiff || 0}
                    </Typography>

                    <Typography 
                        variant="body1"    
                        sx={{
                            animation: 'blinking 1.5s infinite ease-in-out',
                            '@keyframes blinking': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.6 },
                            },
                        }}                                 
                    >
                        {f.state}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color:
                            f.tdDiff < 0
                                ? theme.palette.success.main   
                                : f.tdDiff > 0
                                    ? theme.palette.warning.main   
                                    : theme.palette.text.primary,  
                        }}
                    >
                        Arrival Diff: {f.taDiff}
                    </Typography>
                </FlexBetween>
            </CardContent>
        </Card>
    );
});

const FlightSlotList = ({ flights = [], onClick }) => {
    const theme = useTheme();
    const timeMode = useSelector((state) => state.timemode);
    const user = useSelector((state) => state.user);
    const canView = useMemo(() => 
        hasPermission(user?.permissions, "FlightSlots", "view", false), 
    [user?.permissions]);

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {flights?.map((f, idx) => (
                <FlightCard 
                    key={f._id || idx} 
                    timeMode={timeMode}
                    theme={theme}
                    idx={idx}
                    f={f}
                    canView={canView}
                    onClick={onClick}
                />
            ))}
        </Box>
    );
};

export default React.memo(FlightSlotList);